'use client'
import { MapPinIcon, SmileyIcon, SwapIcon } from '@phosphor-icons/react'
import Image from 'next/image'
import { useState } from 'react'

import { AspectRatio } from '@/components/primitives/AspectRatio/AspectRatio'
import { Heading } from '@/components/primitives/Heading/Heading'
import { cn, getImageUrl } from '@/lib/utils'

import RichTextEditor from '../RichTextEditor/RichTextEditor'

import type { HalfHalfWithTabsType } from '@/types/contentful/halfHalfWithTabsType'

// TO DO: Confirm about contentful icons
const ICONS_MAP = {
  'map-pin': <MapPinIcon className="size-8 text-white lg:size-6" />,
  swap: <SwapIcon className="size-8 text-white lg:size-6" />,
  smiley: <SmileyIcon className="size-8 text-white lg:size-6" />,
}

export const HalfHalfWithTabs = ({ title, subtext, content }: HalfHalfWithTabsType) => {
  const [selected, setSelected] = useState(0)
  const selectedTabs = content?.[selected]

  return (
    <section className={cn('size-full bg-grey-100 px-4 py-8 md:py-16 md:pl-12 md:pr-0 lg:py-24 xl:pl-0')}>
      <div className="flex h-full flex-col items-center justify-between md:flex-row md:items-stretch md:gap-x-8 lg:gap-x-[144px] xl:mx-auto xl:max-w-content xl:px-0 desktop:px-0">
        <div className="flex size-full flex-col text-center md:text-left xl:w-[496px]">
          <div className="mb-6 mt-0 text-primary-500 md:my-8">
            {title && <Heading as="h1">{title}</Heading>}
            {subtext && (
              <div className="mt-2 text-lg leading-7 text-grey-700 md:mt-4">
                <RichTextEditor content={subtext} />
              </div>
            )}
          </div>
          {content && (
            <ul className="grid w-full gap-y-4 xl:w-[496px]">
              {content?.map((item, index) => {
                const { icon, image, description, label } = item?.fields ?? {}
                const imageURL = getImageUrl(image)
                const isSelected = index == selected

                return (
                  <li key={label} onClick={() => setSelected(index)} className={cn('p-4 md:p-6', isSelected && 'w-full rounded-2xl bg-white')}>
                    <div className={cn('flex w-full cursor-pointer')}>
                      <div className="mr-4 flex items-center justify-center md:mr-6">
                        <div className={cn('flex size-12 items-center justify-center rounded-[50%] xl:size-14', isSelected ? 'bg-primary-500' : 'bg-grey-500')}>
                          {ICONS_MAP[icon as keyof typeof ICONS_MAP]}
                        </div>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="mb-1 text-lg font-semibold leading-6 text-grey-900 md:text-xl md:leading-7">{label}</p>
                        <p className="text-base leading-6 text-grey-700">{description}</p>
                      </div>
                    </div>
                    {isSelected && selectedTabs && (
                      <div className="mt-4 block aspect-[5/4] rounded-xl bg-neutral-300 md:hidden ">
                        {imageURL && (
                          <AspectRatio ratio="5/4">
                            <Image
                              width={312}
                              height={320}
                              className="mx-auto aspect-[5/4] w-full rounded-xl object-cover"
                              src={imageURL}
                              alt={selectedTabs?.fields?.image?.fields?.title ?? 'Tab Image'}
                            />
                          </AspectRatio>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        {selectedTabs && (
          <div className="hidden aspect-[5/4] w-full rounded-l-2xl bg-neutral-300  md:block min-[1440px]:rounded-r-2xl">
            {selectedTabs?.fields?.image && (
              <Image
                width={680}
                height={632}
                className="aspect-[5/4] size-full rounded-l-2xl object-cover min-[1440px]:rounded-r-2xl"
                src={getImageUrl(selectedTabs?.fields?.image)}
                alt={selectedTabs?.fields?.image?.fields?.title ?? 'Tab Image'}
              />
            )}
          </div>
        )}
      </div>
    </section>
  )
}
